import { api } from './client';
import type { DraftTelemetry } from '@/hooks/useDraftTelemetry';
import type { RecordingReport } from '@/lib/apiErrorCode';
import { parseGradeHistory } from '@/lib/lessonFeedback';

/**
 * Endpoints implemented by progress-service.
 * See services/progress-service/internal/handler/progress_handler.go.
 */

/**
 * Ответ ученика на одно задание.
 *
 * Форма ОДНА для самого ученика и для разбора куратором — так на сервере
 * (`lessonAnswerItem`). Две формы означали бы два набора типов здесь и два
 * места, где их надо не забыть поправить.
 *
 * ⚠️ `attempts` — единственное, что отличает «понял сразу» от «дожал».
 * Признак `is_correct` липкий: ответив верно с третьей попытки, ученик
 * получает «верно», и по нему одному разницы не видно. Истории попыток нет —
 * в базе одна строка на пару «ученик и задание».
 */
export type LessonAnswer = {
  block_id: string;
  answer_data: string;
  is_correct: boolean;
  block_type?: string;
  attempts?: number;
  time_spent_sec?: number;
  updated_at?: string;
};

/** Один урок ученика в разборе прогресса. */
export type StudentLessonRow = {
  lesson_id: string;
  title: string;
  cover_url?: string;
  module_title?: string;
  is_completed: boolean;
  completed_at?: string;
  /**
   * Оценка за урок, 0–100.
   *
   * ⚠️ `null` ЗНАЧИТ «НЕ ОЦЕНИВАЛИ», А НОЛЬ — «ПОСТАВИЛИ НОЛЬ». Это разные
   * состояния, и рисовать их надо по-разному: прочерк и цифру 0. В домашке
   * такого различения нет — там оценка хранится числом с умолчанием ноль, и
   * «приняли без оценки» неотличимо от «поставили ноль». Повторять эту ошибку
   * здесь нельзя: со шкалой 0–100 ноль стал полноценной оценкой.
   */
  grade?: number | null;
  graded_at?: string;
  /**
   * Оценка и фидбек куратора (D11, 10.09.2026). Сырой вид — разбирает
   * `parseCuratorGrade` из `lib/lessonFeedback.ts`.
   *
   * ⚠️ ОТСУТСТВИЕ ПОЛЯ И `null` — РАЗНОЕ: `null` — «оценки нет», а поля нет у
   * старого сервера, и тогда кураторка остаётся прежней, с одним полем оценки.
   */
  curator_grade?: unknown;
  /**
   * Урок засчитан оценкой сотрудника, а не пройден учеником (договор
   * `lesson-force-complete.md` §4). Нет поля — сервер старый.
   */
  completed_manually?: boolean;
  /**
   * После этого урока сотрудник открыл следующий (§3). `null` — нет. Сырой вид —
   * разбирает `parseNextOpened` из `lib/lessonManualActions.ts`.
   */
  next_opened?: unknown;
  /**
   * Следы работы ученика в уроке (26.09.2026, договор `lesson-force-complete.md` §4):
   * `answers_count` — столько ответов покажет разбор урока (служебный пульс не в счёт),
   * `opened` — в уроке есть хоть какой-то след (ответ, пульс, раздел, домашка).
   * Нет полей — сервер старый: ничего не утверждаем. Разбирает `manualCompletionNote`.
   */
  answers_count?: number;
  opened?: boolean;
};

/**
 * Уроки ученика по всем его действующим курсам, со счётом «пройдено N из M».
 *
 * Счёт приходит с сервера тем же запросом, что и список: одно число, один
 * источник, разъехаться нечему. В знаменатель идут только уроки, которые
 * ученик реально может пройти (опубликованные, с непустым разделом).
 */
export type StudentLessonsOverview = {
  total: number;
  passed: number;
  marathons: {
    marathon_id: string;
    title: string;
    total: number;
    passed: number;
    lessons: StudentLessonRow[];
    /**
     * Кто ставит балл за урок в этом курсе.
     *
     * `curator` — куратор выставляет оценку руками, и только тогда экран
     * показывает поле ввода. `auto` — сто за пройденный урок, поле не нужно.
     * У мини-курсов сервер всегда отдаёт `auto`: режим на них не действует.
     */
    grading_mode?: 'curator' | 'auto';
  }[];
};

export type HomeworkStatus = 'pending' | 'approved' | 'revision' | 'rejected';

export type HomeworkSubmission = {
  id: string;
  student_id: string;
  section_id: string;
  curator_id?: string;
  content: string;
  files?: HomeworkFile[];
  status: HomeworkStatus;
  curator_note?: string;
  /** Новое имя `curator_note` (договор student-api.md §4). Читать через `reviewNoteOf`. */
  review_note?: string;
  // ⚠️ Полей `grade` и `bonus_points` здесь нет намеренно (D15, 10.09.2026): за
  // домашку очки не начисляются, и интерфейс не должен их ни показывать, ни слать.
  // Сервер у старых работ колонки хранит, но это прошлое, а не оценка.
  submitted_at: string;
  reviewed_at?: string;
  // Enriched display fields (backend ListPendingHomeworkEnriched): the curator
  // queue must show real names, not raw UUIDs.
  student_name?: string;
  avatar_url?: string;
  section_title?: string;
  lesson_title?: string;
  // Пересдача (договор homework-resubmit.md §2, 11.09.2026). Урок раздела — для
  // «Переделать»; `null`, если сервер урока не нашёл.
  lesson_id?: string | null;
  // Текущий круг начался после «на доработку» или «отклонено». Остаётся true и
  // после новой проверки — до следующего входа в очередь не из доработки.
  resubmitted?: boolean;
  resubmitted_at?: string | null;
};

export type HomeworkFile = {
  id: string;
  submission_id: string;
  file_url: string;
  file_type: string;
  file_name: string;
};

export type StudentStats = {
  total_points: number;
  current_streak: number;
  longest_streak: number;
  last_activity_at?: string;
};

export type LeaderboardEntry = {
  rank: number;
  student_id: string;
  student_name?: string;
  avatar_url?: string;
  total_points: number;
};

export type MarathonProgress = {
  marathon_id: string;
  lessons_total: number;
  lessons_completed: number;
  points_earned: number;
  percent_complete: number;
  /**
   * Оценки куратора по урокам (D11, 10.09.2026, решение владельца: ученик берёт
   * их отсюда). У старого сервера поля нет — чипов просто не будет.
   * Разбирает `curatorGradesByLesson` из `lib/lessonFeedback.ts`.
   *
   * `grade` — новое имя `curator_grade` (договор student-api.md §4), тоже объект.
   */
  lessons?: { lesson_id: string; curator_grade?: unknown; grade?: unknown }[];
};

/**
 * Баллы ученика для сотрудника: общая сумма и разбивка по курсам (договор
 * `student-points.md` §2).
 *
 * ⚠️ `courses` — ВСЕГДА массив, у ученика без баллов пустой. Сервер отдаёт `[]`
 * через `make`, а не `null`, ровно чтобы его можно было перебирать без проверки.
 *
 * ⚠️ ЧИТАТЬ `courses` ПО ИМЕНИ, А НЕ `items`. Клиент API приписывает в ответ
 * псевдоним `items` — первый массив в теле (`normalizeList`, `api/client.ts:73`).
 * Здесь это тот же `courses`, но поле побочное и может исчезнуть.
 */
export type StudentPointsCourse = {
  marathon_id: string;
  /** Название курса; пустая строка, если курса уже нет в базе вовсе. */
  title: string;
  points: number;
  /** Курс удалён, а заработанные за него баллы остались. */
  deleted: boolean;
};

export type StudentPoints = {
  student_id: string;
  /**
   * Все баллы ученика: все причины, все курсы. Ровно то число, которое ученик
   * видит у себя (`/progress/stats/me`), — чтобы куратор и ученик не называли
   * разные цифры.
   */
  total_points: number;
  /** По убыванию баллов, сортирует сервер. Курсов с нулём здесь нет. */
  courses: StudentPointsCourse[];
  /** Баллы вне курсов: материалы, бонус за серию, остатки начислений. */
  outside_courses: number;
};

export const progressApi = {
  // ----- Student -----
  async getMyStats() {
    const { data } = await api.get('/progress/stats/me');
    return data as StudentStats;
  },
  async startLesson(lessonId: string, marathonId: string) {
    // Backend StartLesson binds `marathon_id` with binding:"required"; sending
    // an empty body returns 400. The lesson player must pass the marathon it
    // belongs to.
    await api.post(`/progress/lessons/${lessonId}/start`, { marathon_id: marathonId });
  },
  async completeSection(sectionId: string) {
    await api.post(`/progress/sections/${sectionId}/complete`);
  },
  // Saved answers for a lesson, so the player restores the student's work after
  // a page refresh (answers were persisted but not re-hydrated → looked lost).
  async getMyLessonAnswers(lessonId: string) {
    const { data } = await api.get(`/progress/lessons/${lessonId}/answers`);
    return data as LessonAnswer[];
  },

  // ─── Разбор прогресса ученика: куратор и админ ──────────────────────────
  //
  // ⚠️ ПУТИ НАЧИНАЮТСЯ С `/progress/students/`, А НЕ С `/curator/students/`.
  // В карте шлюза строка `~^/api/v1/curator/(students|enrollments|profile|
  // marathons|groups)` уводит ВСЁ, что начинается с `/curator/students/`, в
  // users-service. Ручка отвечала бы исправно, а приложение получало бы 404 —
  // ровно это случилось 10.09.2026 с четырьмя ручками админки. Префикс
  // `/progress/` карта уже отдаёт службе прогресса, править её не нужно.

  /**
   * Баллы одного ученика — для карточки (договор `student-points.md` §2).
   *
   * ⚠️ ОТКАЗ НЕ ПРЕВРАЩАЕМ В НОЛЬ. Сервер на сбое чтения отвечает 500 намеренно:
   * ноль вместо правды — ровно тот дефект, с которого началась эта задача.
   * Ошибку пробрасываем, экран показывает её словами.
   */
  async getStudentPoints(studentId: string) {
    const { data } = await api.get(`/progress/students/${studentId}/points`);
    return data as StudentPoints;
  },

  /**
   * Суммы баллов сразу по списку учеников — для списка у куратора.
   *
   * ⚠️ НЕ БОЛЬШЕ 500 ID ЗА РАЗ, иначе 400 (договор §3). Нарезку по пачкам
   * делает `chunkIds` из `lib/studentPoints.ts`: кураторский список грузит всех
   * учеников одним запросом без пагинации, и у куратора их бывает 850.
   *
   * ⚠️ ЧУЖОГО УЧЕНИКА В ОТВЕТЕ ПРОСТО НЕТ, а свой без начислений приходит с
   * нулём: «о нём не спрашивали» и «у него ноль» — разные вещи.
   */
  async getStudentsPoints(ids: string[]): Promise<Record<string, number>> {
    const { data } = await api.get('/progress/student-points', { params: { ids: ids.join(',') } });
    return ((data as { points?: Record<string, number> })?.points ?? {}) as Record<string, number>;
  },

  /** Уроки ученика с отметкой «пройден» и счётом «пройдено N из M». */
  async getStudentLessons(studentId: string) {
    const { data } = await api.get(`/progress/students/${studentId}/lessons`);
    return data as StudentLessonsOverview;
  },
  /** Ответы ученика по уроку — что выбрал, верно ли, со скольких попыток. */
  async getStudentLessonAnswers(studentId: string, lessonId: string) {
    const { data } = await api.get(`/progress/students/${studentId}/lessons/${lessonId}/answers`);
    return data as LessonAnswer[];
  },
  /** Разделы урока, пройденные этим учеником. */
  async getStudentCompletedSections(studentId: string, lessonId: string): Promise<string[]> {
    const { data } = await api.get(
      `/progress/students/${studentId}/lessons/${lessonId}/completed-sections`,
    );
    return ((data as { items?: string[] })?.items ?? []) as string[];
  },
  /**
   * Поставить оценку за урок, 0–100.
   *
   * ⚠️ Сервер отвечает 409, если урок не пройден или курс не в режиме
   * `curator`. Интерфейс обязан не предлагать того, что будет отвергнуто:
   * поле показывается только при `grading_mode === 'curator'` и только у
   * пройденного урока.
   */
  async setLessonGrade(studentId: string, lessonId: string, grade: number) {
    await api.put(`/progress/students/${studentId}/lessons/${lessonId}/grade`, { grade });
  },
  /**
   * Снять оценку: урок снова неоценённый.
   *
   * Отдельно от «поставить ноль» — это разные действия. Ноль в шкале 0–100 —
   * настоящая оценка, и отменить ошибочно выставленный балл можно только
   * снятием, а не заменой на ноль.
   */
  async clearLessonGrade(studentId: string, lessonId: string) {
    await api.delete(`/progress/students/${studentId}/lessons/${lessonId}/grade`);
  },
  /**
   * Оценка и фидбек за урок одной ручкой (D11, 10.09.2026). `grade: null` —
   * «без оценки», `feedback: null` — «без текста».
   *
   * ⚠️ Путь под `/progress/students/`, а не `/curator/students/` из первого
   * текста контракта — решение владельца 10.09.2026: второй шлюз увёл бы в
   * users-service. 409 с `code: grading_mode_auto` — курс в автоматическом
   * режиме (`isGradingModeAutoError`).
   */
  async saveLessonFeedback(studentId: string, lessonId: string, body: { grade: number | null; feedback: string | null }) {
    const { data } = await api.put(`/progress/students/${studentId}/lessons/${lessonId}/grade`, body);
    return data as {
      grade: number | null;
      feedback: string | null;
      updated_at: string;
      updated_by?: { id: string; name: string };
      /**
       * Урок засчитан ЭТИМ сохранением (договор `lesson-force-complete.md` §2.2):
       * в курсе «оценивает куратор» оценка у непройденного урока засчитывает его и
       * открывает следующий. `false` — урок был пройден и раньше.
       */
      completed_now?: boolean;
      is_completed?: boolean;
    };
  },
  /**
   * «Открыть следующий» в курсе «оценивает система» и в мини-курсе (§3).
   *
   * `lessonId` — урок, на котором ученик застрял, а не следующий. Текущий урок
   * остаётся непройденным, баллов нет. Повторное нажатие — тоже 200 с `already`.
   *
   * ⚠️ Путь под `/progress/students/`, как у оценки: `/curator/students/` шлюз
   * уводит в users-service.
   */
  async openNextLesson(studentId: string, lessonId: string) {
    const { data } = await api.post(`/progress/students/${studentId}/lessons/${lessonId}/open-next`);
    return data as {
      lesson_id: string;
      next_opened: boolean;
      already: boolean;
      opened_by?: { id: string; name: string };
      opened_at: string;
    };
  },
  /** История правок оценки и фидбека: новые сверху. */
  async getLessonGradeHistory(studentId: string, lessonId: string) {
    const { data } = await api.get(`/progress/students/${studentId}/lessons/${lessonId}/grade/history`);
    return parseGradeHistory(data);
  },
  /**
   * Ответ на задание.
   *
   * ⚠️ ТЕЛО ОТВЕТА ВОЗВРАЩАЕТСЯ, А НЕ ВЫБРАСЫВАЕТСЯ (12.09.2026). У голосового
   * ответа сервер кладёт в `data.recording` длину записи, померенную ПО ЗВУКУ, —
   * ту самую, которой не хватало ученице: она видела на экране 2:30 (свои часы),
   * а в файле было 20 секунд. Пока ответ сервера здесь выбрасывался, достать это
   * число было физически неоткуда.
   *
   * ⚠️ В пояснениях этого файла нельзя писать вызов клиента буквально: сторож
   * путей (`adminRoutesInGateway.test.ts`) читает файл целиком и принял бы текст
   * из комментария за настоящий вызов с путём из переменной.
   *
   * `client_seconds` и `stop_reason` — необязательные, договор `record-integrity.md`
   * §2.1. Старый сервер их просто игнорирует, поэтому порядок выката не важен.
   */
  async answerBlock(
    blockId: string,
    payload: {
      answer_data: string;
      is_correct: boolean;
      block_type?: string;
      client_seconds?: number;
      stop_reason?: string;
      /**
       * Имя записи голоса и сколько она пролежала (договор `audio-upload.md` §3, 02.10.2026). По ним сервер
       * узнаёт повтор той же записи и отказывает опоздавшей (409). Без них поведение прежнее.
       */
      client_id?: string;
      age_ms?: number;
    },
  ): Promise<{ recording?: RecordingReport; replayed?: boolean } | undefined> {
    const { data } = await api.post(`/progress/blocks/${blockId}/answer`, payload);
    return data as { recording?: RecordingReport; replayed?: boolean } | undefined;
  },
  /**
   * Известие о записи голоса, которая ещё не дошла (договор `audio-upload.md` §4): несколько сот байт
   * вместо мегабайтов — проходит там, где сама загрузка не проходит. Сервер так видит недошедшее.
   *
   * ⚠️ ОТКАЗ И ОБРЫВ ГЛОТАЮТСЯ МОЛЧА: известие не должно ни встать в очередь впереди самой записи, ни
   * показаться ученику.
   */
  recordingAttempt(
    blockId: string,
    body: {
      client_id: string;
      phase: 'queued' | 'upload_failed' | 'dropped';
      age_ms?: number;
      size_bytes?: number;
      detail?: string;
    },
  ): void {
    try {
      void api.post(`/progress/blocks/${blockId}/recording-attempt`, body).catch(() => undefined);
    } catch {
      /* известие не должно ронять отправку */
    }
  },
  async heartbeat(blockId: string) {
    // fire-and-forget — used by the lesson player to track time-on-content.
    api.post(`/progress/blocks/${blockId}/heartbeat`).catch(() => {});
  },

  // Homework
  async listMyHomework(status?: HomeworkStatus, offset = 0, limit = 20) {
    const { data } = await api.get('/progress/homework', { params: { status, offset, limit } });
    return data as { items: HomeworkSubmission[]; total: number };
  },
  async submitHomework(payload: {
    section_id: string;
    content: string;
    file_urls?: string[];
    /** Счётчики о том, как набирался текст. Видит только куратор. */
    draft?: DraftTelemetry;
  }) {
    const { data } = await api.post('/progress/homework', payload);
    return data as HomeworkSubmission;
  },
  async getHomework(id: string) {
    const { data } = await api.get(`/progress/homework/${id}`);
    return data as HomeworkSubmission;
  },

  // Leaderboard + rank
  async getLeaderboard(marathonId?: string, limit = 10) {
    const { data } = await api.get('/progress/leaderboard', {
      params: { marathon_id: marathonId, limit },
    });
    return data as LeaderboardEntry[];
  },
  /**
   * Рейтинг в том виде, в каком его видит ученик: верхушка плюс он сам с
   * соседями.
   *
   * ⚠️ ОКНО СЧИТАЕТ СЕРВЕР. Раньше экран забирал первые 50 строк и рисовал их
   * подряд: на потоке в 814 учеников человек с двухсотым местом не видел ни
   * себя, ни соседей — только чужую верхушку. Тянуть весь список ради пяти
   * строк — то же самое, но дороже.
   */
  async getLeaderboardWindow(marathonId: string) {
    const { data } = await api.get('/progress/leaderboard/window', {
      params: { marathon_id: marathonId },
    });
    return data as {
      top: LeaderboardEntry[];
      around: LeaderboardEntry[];
      my_rank: number;
      total: number;
    };
  },
  async getMyRank(marathonId?: string) {
    const { data } = await api.get('/progress/rank', { params: { marathon_id: marathonId } });
    return data as { rank: number };
  },
  /**
   * Разделы урока, которые ученик уже прошёл.
   *
   * Плеер урока держал этот список только в памяти вкладки, и при возврате к
   * уроку прогресс выглядел обнулённым: «Пройдено разделов: 0 / 9» при восьми
   * пройденных на сервере.
   */
  async getCompletedSections(lessonId: string): Promise<string[]> {
    const { data } = await api.get(`/progress/lessons/${lessonId}/completed-sections`);
    return ((data as { items?: string[] })?.items ?? []) as string[];
  },

  async getMyMarathonProgress(marathonId: string) {
    const { data } = await api.get(`/progress/marathons/${marathonId}/my-progress`);
    // Backend now returns lessons_total + percent_complete alongside the
    // aggregated counts. We still defensively default each field so an older
    // backend (pre-migration) keeps the page contract intact.
    const raw = (data ?? {}) as {
      completed_lessons?: number;
      lessons_completed?: number;
      lessons_total?: number;
      total_points?: number;
      points_earned?: number;
      percent_complete?: number;
      lessons?: unknown;
    };
    const lessonsCompleted = raw.lessons_completed ?? raw.completed_lessons ?? 0;
    return {
      marathon_id: marathonId,
      lessons_total: raw.lessons_total ?? 0,
      lessons_completed: lessonsCompleted,
      points_earned: raw.points_earned ?? raw.total_points ?? 0,
      percent_complete: raw.percent_complete ?? 0,
      // ⚠️ Оценки куратора по урокам (D11). Адаптер собирает ответ заново, и до
      // 10.09.2026 это поле здесь молча терялось: чипы у ученика не появились бы
      // и с готовым сервером. Сторож — `myProgressLessons.test.ts`.
      lessons: Array.isArray(raw.lessons) ? (raw.lessons as MarathonProgress['lessons']) : undefined,
    } as MarathonProgress;
  },

  // ----- Curator -----
  /**
   * Очередь домашек куратора (админу — всех). `q` ищет по всей очереди на сервере:
   * ученик (имя, почта), раздел, урок, курс; `total` — число найденных (28.09.2026).
   */
  async listPendingHomework(status?: HomeworkStatus, offset = 0, limit = 20, q?: string) {
    const { data } = await api.get('/curator/homework', { params: { status, offset, limit, q: q || undefined } });
    return data as { items: HomeworkSubmission[]; total: number };
  },
  /** Одна сдача с полями строки очереди — окно проверки по ссылке из уведомления. */
  async getStaffHomework(id: string) {
    const { data } = await api.get(`/curator/homework/${id}`);
    return data as HomeworkSubmission;
  },
  /**
   * Проверить домашнюю работу: решение и необязательный комментарий.
   *
   * ⚠️ Без `grade` и `bonus_points` (D15, 10.09.2026): очков за домашку нет ни
   * в одном режиме курса, сервер эти поля игнорирует.
   */
  async reviewHomework(id: string, payload: {
    status: HomeworkStatus;
    note?: string;
  }) {
    await api.put(`/curator/homework/${id}/review`, payload);
  },
};

/**
 * Описание запроса баллов одного ученика — общее для карточки и для плашки над
 * ней.
 *
 * ⚠️ КЛЮЧ ОДИН НА ОБА МЕСТА НАМЕРЕННО. Карточка ученика показывает сумму дважды:
 * плашкой в шапке и плиткой, а под ними — разбивку. Разойдись ключи хоть буквой,
 * react-query сходит за баллами дважды и, что хуже, в двух местах экрана могут
 * оказаться числа из разных ответов.
 *
 * ⚠️ `staleTime` ЗДЕСЬ НЕ УКРАШЕНИЕ, А ЧАСТЬ ТОГО ЖЕ ПРАВИЛА. Одного ключа мало:
 * при умолчании `staleTime: 0` данные считаются устаревшими сразу, и второй
 * наблюдатель, встающий позже первого (разбивка появляется после того, как
 * загрузился сам ученик), запускает повторный запрос. Нашлось проверкой
 * `curatorStudentPoints.test.ts`: запросов уходило два вместо одного. Полминуты
 * — заведомо больше зазора между двумя частями одного экрана и заведомо меньше
 * времени, за которое баллы у ученика успевают измениться.
 */
export const studentPointsQuery = (studentId: string | undefined) => ({
  queryKey: ['progress', 'student-points', studentId] as const,
  queryFn: () => progressApi.getStudentPoints(studentId!),
  enabled: !!studentId,
  staleTime: 30_000,
});
